import { useEffect, useState } from "react";
import { Alert, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect, FilterDate, ClaimLink, money } from "../components/reports/reportShared.jsx";
import { getNonApprovedInvoicesReport, listEntityRows, listUsers } from "../services/apiClient.js";

// Non Approved Contractor Invoices (legacy report): one row per unapproved
// contractor invoice (amount > 0), grouped by PCO. 1.0 fixes: the hardcoded
// "created after 1 Jun 2023" cutoff became an optional From date; the
// must-have-attached-file condition isn't ported (2.0 doesn't store files on
// contractor invoices); parameterised.

function NonApprovedInvoicesReportPage() {
  const [filters, setFilters] = useState({
    from: "", division_id: "", project_coordinator_id: "", job_type_id: "", contractor_company_id: "",
  });
  const [lookups, setLookups] = useState({});
  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all(
      ["divisions", "job-types", "contractor-companies"].map((k) =>
        listEntityRows(k).then((rows) => [k, rows]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(f = filters) {
    setError("");
    try {
      setReport(await getNonApprovedInvoicesReport(f));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the report.");
    }
  }
  const set = (name) => (value) => {
    const next = { ...filters, [name]: value };
    setFilters(next);
    load(next);
  };

  const totalAmount = (report?.rows || []).reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <section className="page-panel">
      <div className="page-heading"><h2>Non Approved Contractor Invoices <RulesHelp topic="nonApprovedInvoices" /></h2></div>

      <div className="d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-3 no-print">
        <FilterSelect label="Division" value={filters.division_id} onChange={set("division_id")} options={lookups.divisions} width={150} />
        <FilterSelect label="Project Co-Ordinator" value={filters.project_coordinator_id} onChange={set("project_coordinator_id")} options={users} labelField="full_name" width={200} />
        <FilterSelect label="Job Type" value={filters.job_type_id} onChange={set("job_type_id")} options={lookups["job-types"]} />
        <FilterSelect label="Contractor" value={filters.contractor_company_id} onChange={set("contractor_company_id")} options={lookups["contractor-companies"]} width={200} />
        <FilterDate label="Created after" value={filters.from} onChange={set("from")} />
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm" className="align-middle">
            <thead className="table-surface">
              <tr>
                <th>Job #</th>
                <th>Order #</th>
                <th>Project Co-Ordinator</th>
                <th>Site Address</th>
                <th>Contractor</th>
                <th>Inv #</th>
                <th>Comments</th>
                <th className="text-end text-nowrap">Order Received Total</th>
                <th className="text-end text-nowrap">This Invoice</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r, i) => (
                <tr key={i}>
                  <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="orders" /></td>
                  <td>{r.order_number ?? r.order_seq ?? "-"}</td>
                  <td className="text-nowrap">{r.project_coordinator_name || "-"}</td>
                  <td className="text-truncate" style={{ maxWidth: 200 }} data-hint={r.street_address || undefined}>{r.street_address || "-"}</td>
                  <td className="text-nowrap">
                    {r.contact_email ? (
                      <a href={`mailto:${r.contact_email}`} data-hint={r.contact_email}>{r.contractor_name}</a>
                    ) : (
                      r.contractor_name || "-"
                    )}
                  </td>
                  <td className="text-nowrap">{r.invoice_number || "-"}</td>
                  <td className="text-truncate" style={{ maxWidth: 180 }} data-hint={r.comments || undefined}>{r.comments || "-"}</td>
                  <td className="text-end text-nowrap">{money(r.invoices_received)}</td>
                  <td className="text-end text-nowrap fw-semibold">{money(r.amount)}</td>
                </tr>
              ))}
              {report.rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-muted">No unapproved contractor invoices.</td></tr>
              ) : (
                <tr className="table-surface fw-semibold">
                  <td colSpan={8} className="text-end">Total awaiting approval</td>
                  <td className="text-end">{money(totalAmount)}</td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="small text-muted">{report.rows.length} invoice(s).</div>
        </>
      )}
    </section>
  );
}

export default NonApprovedInvoicesReportPage;
